// src/components/CategoryList.tsx

import React, { useCallback, useContext, useEffect, useState } from "react";
import { ContentContext } from "../../helper/ContentContext"; // Adjust the import path as needed
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { ScrollArea } from "../../../components/ui/scroll-area";
import { Table, TableCell, TableHead, TableRow } from "../../../components/ui/table";
import {
	CaretSortIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	DownloadIcon,
	GlobeIcon,
	ImageIcon,
	Pencil2Icon,
	QuestionMarkCircledIcon,
	TrashIcon,
} from "@radix-ui/react-icons";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from "../../../components/ui/drawer";
import { Card } from "../../../components/ui/card";
import ContentCategory from "../../../arcView/types/ContentCategory";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../../../components/ui/dialog";

const ContentControl: React.FC = () => {
	const { categories, fetchCategories, addCategory, deleteCategory, uploadImages, deleteImage } =
		useContext(ContentContext);
	const [newCategoryName, setNewCategoryName] = useState("");
	const [openDrawerId, setOpenDrawerId] = useState<number | null>(null);
	const [sortBy, setSortBy] = useState<string>("name");
	const [sortDirection, setSortDirection] = useState<string>("ASC");
	const [selectedSort, setSelectedSort] = useState<string | undefined>(undefined);

	const handleAddCategory = async () => {
		if (newCategoryName.trim() === "") return;

		await addCategory(newCategoryName);
		setNewCategoryName("");
	};

	const handleDeleteCategory = async (id: number) => {
		await deleteCategory(id);
	};

	const memoizedFetchCategories = useCallback(() => {
		fetchCategories(sortBy, sortDirection);
	}, [sortBy, sortDirection, fetchCategories]);

	useEffect(() => {
		memoizedFetchCategories();
	}, [categories]);

	const handleSortChange = (field: string) => {
		const isAsc = sortBy === field && sortDirection === "ASC";
		setSortBy(field);
		setSortDirection(isAsc ? "DESC" : "ASC");
	};

	const getSortIcon = (field: string) => {
		if (sortBy === field) {
			return sortDirection === "ASC" ? <ChevronUpIcon className="ml-2" /> : <ChevronDownIcon className="ml-2" />;
		}
		return <CaretSortIcon className="ml-2" />;
	};

	return (
		<div className="flex flex-col items-start justify-start p-12">
			<h1 className="font-medium text-2xl mb-8">Kategorien</h1>
			<div className="flex items-start justify-start w-1/4 gap-4">
				<Input
					type="text"
					value={newCategoryName}
					onChange={(e) => setNewCategoryName(e.target.value)}
					placeholder="Neue Kategorie"
				/>
				<Button onClick={handleAddCategory}>Kategorie anlegen</Button>
			</div>
			<ScrollArea className="w-full mt-8 p-4 whitespace-nowrap rounded-md border">
				<Table className="px-8 z-40">
					<TableHead onClick={() => handleSortChange("name")} className="cursor-pointer w-[100px]">
						<div className="flex items-center whitespace-nowrap w-max">
							<span className="text-sm font-bold w-full">Alle Kategorien</span>
							{getSortIcon("name")}
						</div>
					</TableHead>
					<TableHead className="w-1/6">
						<span className="text-sm font-bold">Aktionen</span>
					</TableHead>
					{Array.isArray(categories) &&
						categories.map((category) => (
							<TableRow key={category.id}>
								<TableCell>
									<div className="flex items-center justify-start gap-6">
										<span className="p-2 border border-border rounded-lg font-semibold">
											{category.name}
										</span>
									</div>
								</TableCell>
								<TableCell>
									<div className="flex items-center justify-start gap-2">
										{/* Edit Category Drawer */}
										<Drawer
											open={openDrawerId === category.id}
											onOpenChange={(open) => setOpenDrawerId(open ? category.id : null)}
										>
											<DrawerTrigger asChild>
												<Button
													variant="outline"
													className="flex items-center gap-2"
													onClick={() => setOpenDrawerId(category.id)}
												>
													<Pencil2Icon className="w-5 h-5" />
												</Button>
											</DrawerTrigger>
											<DrawerContent className="h-[95vh] z-[90]">
												<ScrollArea className="h-[90vh]">
													<div className="mx-auto w-full max-w-2xl">
														<DrawerHeader>
															<DrawerTitle>Kategorie: {category.name}</DrawerTitle>
															<DrawerDescription>
																Nehmen Sie hier alle gewünschten Änderungen vor.
															</DrawerDescription>
														</DrawerHeader>
														<CategoryEditDrawer
															category={category}
															uploadImages={uploadImages}
															fetchCategories={fetchCategories}
															onClose={() => setOpenDrawerId(null)}
														/>
														<div className="grid grid-cols-3 my-8">
															{Array.isArray(category.images) &&
																category.images.map((image) => (
																	<div key={image.id} className="relative">
																		<img
																			src={`${image.imageUrl}`}
																			alt={image.title}
																			className="w-full h-auto object-cover rounded-md"
																		/>
																		<button
																			className="absolute top-2 right-2 p-1 bg-red-500 rounded-full hover:cursor-pointer"
																			onClick={() => deleteImage(image.id)}
																		>
																			<TrashIcon className="text-white" />
																		</button>
																	</div>
																))}
														</div>
														<DrawerFooter>
															<DrawerClose asChild>
																<Button variant="outline">Schließen</Button>
															</DrawerClose>
														</DrawerFooter>
													</div>
												</ScrollArea>
											</DrawerContent>
										</Drawer>
										{/* Delete Category Button */}
										<DeleteCategoryDialog
											category={category}
											handleDeleteCategory={handleDeleteCategory}
										/>
									</div>
								</TableCell>
							</TableRow>
						))}
				</Table>
			</ScrollArea>
		</div>
	);
};

interface CategoryEditDrawerProps {
	category: ContentCategory;
	uploadImages: (categoryId: number, images: File[], title?: string) => Promise<void>;
	fetchCategories: () => void;
	onClose: () => void;
}

const CategoryEditDrawer: React.FC<CategoryEditDrawerProps> = ({
	category,
	uploadImages,
	fetchCategories,
	onClose,
}) => {
	const [selectedImages, setSelectedImages] = useState<File[]>([]);
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const [title, setTitle] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const handleImageHoldForUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files || []);
		setSelectedImages((prevImages) => [...prevImages, ...files]);
	};

	const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		const files = Array.from(event.dataTransfer.files);
		setSelectedImages((prevImages) => [...prevImages, ...files]);
		setIsDragging(false);
	};

	const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
	};

	const handleDragEnter = () => {
		setIsDragging(true);
	};

	const handleDragLeave = () => {
		setIsDragging(false);
	};

	const removeImage = (indexToRemove: number) => {
		setSelectedImages((prevImages) => prevImages.filter((_, index) => index !== indexToRemove));
	};

	const handleAddImageToCategory = async () => {
		if (selectedImages.length === 0) return;
		setIsLoading(true);
		try {
			await uploadImages(category.id, selectedImages, title);
			setSelectedImages([]);
			fetchCategories();
			onClose();
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div>
			<Card className="w-full h-full gap-4 py-8 px-16 items-start justify-start">
				<h3 className="mb-2 text-lg font-medium">Bilder hochladen</h3>
				<div className="py-3">
					<label htmlFor="imageTitle">Name</label>
					<span className="flex items-center justify-center gap-2">
						<Input
							id="imageTitle"
							type="text"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							required
						/>
						<TooltipProvider>
							<Tooltip>
								<TooltipTrigger asChild>
									<Button variant="outline">
										<QuestionMarkCircledIcon />
									</Button>
								</TooltipTrigger>
								<TooltipContent>
									<p>
										Der übergebene Titel wird auf jedes Bild gleichermaßen angewendet.
										<br />
										Sollte es sich um einzigartige Bilder handeln mit besonderer Bezeichnung,
										<br />
										sollten diese separat hochgeladen werden.
									</p>
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					</span>
				</div>
				<div
					className={`border-2 border-dashed border-gray-400 p-4 rounded-lg w-full flex justify-center items-center ${
						isDragging ? "bg-gray-200" : "bg-transparent"
					}`}
					onDrop={handleDrop}
					onDragOver={handleDragOver}
					onDragEnter={handleDragEnter}
					onDragLeave={handleDragLeave}
				>
					{isDragging ? (
						<p className="text-gray-800 flex items-center justify-center gap-4">
							<DownloadIcon className="w-16 h-16" />
							Lassen Sie die Dateien los, um sie hochzuladen
						</p>
					) : (
						<label className="cursor-pointer text-gray-500">
							<span className="flex items-center justify-center gap-4">
								<ImageIcon className="w-16 h-16" />
								Ziehen Sie Ihre Bilder hierher oder klicken Sie
							</span>
							<input
								type="file"
								accept="image/*"
								multiple
								onChange={handleImageHoldForUpload}
								className="hidden"
							/>
						</label>
					)}
				</div>
				{selectedImages.length > 0 && (
					<div className="mt-4 grid grid-cols-2 gap-4">
						{selectedImages.map((image, index) => {
							const imageUrl = URL.createObjectURL(image);
							return (
								<div key={index} className="relative">
									<img
										src={imageUrl}
										alt={`preview-${index}`}
										className="w-full h-auto object-cover rounded-md"
									/>
									<button
										className="absolute right-1 top-1 p-1 bg-red-500 rounded-full hover:cursor-pointer"
										onClick={() => removeImage(index)}
									>
										<TrashIcon className="text-white" />
									</button>
								</div>
							);
						})}
					</div>
				)}
			</Card>
			<Button
				onClick={handleAddImageToCategory}
				disabled={selectedImages.length === 0 || isLoading}
				className="mt-4 w-full"
			>
				{isLoading ? "Speichert..." : "Änderungen speichern"}
			</Button>
		</div>
	);
};

const DeleteCategoryDialog: React.FC<{
	category: { id: number; name: string };
	handleDeleteCategory: (id: number) => void;
}> = ({ category, handleDeleteCategory }) => {
	const [inputValue, setInputValue] = useState<string>("");
	const isDeleteEnabled = inputValue === category.name;

	return (
		<Dialog>
			<DialogTrigger>
				<Button variant={"destructive"}>
					<TrashIcon />
				</Button>
			</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Sie sind im Begriff, diese Kategorie zu löschen!</DialogTitle>
					<DialogDescription>
						Bitte geben Sie den Namen der Kategorie <strong>{category.name}</strong> ein, um das Löschen zu
						bestätigen. Dieser Vorgang ist nicht umkehrbar, auch bei einem Versehen.
					</DialogDescription>
				</DialogHeader>
				<div className="my-4">
					<Input
						type="text"
						placeholder={`Geben Sie "${category.name}" ein, um zu bestätigen`}
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						className="w-full px-4 py-2 border rounded-md"
					/>
				</div>
				<DialogFooter className="sm:justify-start">
					<DialogClose asChild>
						<div className="flex items-center justify-between gap-2 w-full">
							<Button
								variant="destructive"
								className="flex items-center gap-2 w-full"
								onClick={() => handleDeleteCategory(category.id)}
								disabled={!isDeleteEnabled}
							>
								Kategorie endgültig löschen
							</Button>
							<Button type="button" variant="secondary" className="w-full">
								Abbrechen
							</Button>
						</div>
					</DialogClose>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default ContentControl;
